package com.videoanalyzer.web.components

import androidx.compose.runtime.Composable
import com.videoanalyzer.web.models.AnalysisSummary
import com.videoanalyzer.web.state.formatDate
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * History list showing past analyses.
 *
 * ANDROID EQUIVALENT: RecyclerView with AnalysisAdapter
 */
@Composable
fun HistoryScreen(
    analyses: List<AnalysisSummary>,
    total: Int,
    hasMore: Boolean,
    isLoading: Boolean,
    onViewAnalysis: (String) -> Unit,
    onLoadMore: () -> Unit
) {
    Div({
        classes("fade-in")
        style {
            maxWidth(900.px)
            margin(0.px, "auto".unsafeCast<CSSNumeric>())
            padding(32.px, 24.px)
        }
    }) {
        // Header
        Div({
            classes("fade-in-up")
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.SpaceBetween)
                marginBottom(24.px)
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(12.px)
                }
            }) {
                // Simple icon - no animation
                Span({
                    style {
                        fontSize(24.px)
                    }
                }) { Text("📋") }

                Div {
                    H1({
                        style {
                            fontSize(20.px)
                            fontWeight(600)
                            color(Color("#FAFAFA"))
                            marginBottom(2.px)
                        }
                    }) { Text("Analysis History") }

                    Span({
                        style {
                            color(Color("#A1A1AA"))
                            fontSize(13.px)
                        }
                    }) { Text("$total total analyses") }
                }
            }

            // Stats badge - neutral styling
            if (total > 0) {
                Div({
                    style {
                        padding(6.px, 14.px)
                        backgroundColor(Color("rgba(255, 255, 255, 0.05)"))
                        borderRadius(50.px)
                    }
                }) {
                    Span({
                        style {
                            color(Color("#FAFAFA"))
                            fontSize(12.px)
                            fontWeight(500)
                        }
                    }) { Text("$total videos analyzed") }
                }
            }
        }

        if (analyses.isEmpty() && !isLoading) {
            // Empty state
            EmptyHistoryState()
        } else {
            // Analysis list
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(16.px)
                }
            }) {
                analyses.forEachIndexed { index, analysis ->
                    Div({
                        classes("fade-in-up", "stagger-${(index % 5) + 1}")
                    }) {
                        HistoryItem(analysis, onViewAnalysis)
                    }
                }
            }

            // Load more button
            if (hasMore) {
                Div({
                    classes("fade-in-up")
                    style {
                        marginTop(32.px)
                        textAlign("center")
                    }
                }) {
                    Button({
                        classes("btn", "btn-secondary")
                        style {
                            padding(14.px, 40.px)
                            fontSize(15.px)
                            fontWeight(600)
                            property("background", "rgba(51, 65, 85, 0.6)")
                            property("border", "1px solid rgba(255, 255, 255, 0.1)")
                        }
                        onClick { onLoadMore() }
                        if (isLoading) disabled()
                    }) {
                        if (isLoading) {
                            Div({
                                style {
                                    display(DisplayStyle.Flex)
                                    alignItems(AlignItems.Center)
                                    gap(10.px)
                                }
                            }) {
                                InlineLoading()
                                Text("Loading more...")
                            }
                        } else {
                            Span({
                                style {
                                    display(DisplayStyle.Flex)
                                    alignItems(AlignItems.Center)
                                    gap(8.px)
                                }
                            }) {
                                Text("Load More")
                                Span({ style { fontSize(16.px) } }) { Text("↓") }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun HistoryItem(
    analysis: AnalysisSummary,
    onView: (String) -> Unit
) {
    Div({
        classes("card")
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(16.px)
            cursor("pointer")
            padding(16.px, 20.px)
        }
        onClick { onView(analysis.id) }
    }) {
        // Score indicator - no glow
        ScoreCircle(analysis.overallScore)

        // Video info
        Div({
            style {
                property("flex", "1")
                minWidth(0.px) // Allow text truncation
            }
        }) {
            // YouTube badge
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(6.px)
                    marginBottom(4.px)
                }
            }) {
                Span({
                    style {
                        backgroundColor(Color("rgba(239, 68, 68, 0.1)"))
                        color(Color("#EF4444"))
                        padding(2.px, 6.px)
                        borderRadius(3.px)
                        fontSize(9.px)
                        fontWeight(600)
                        letterSpacing(0.3.px)
                    }
                }) { Text("YOUTUBE") }
            }

            H3({
                style {
                    fontSize(14.px)
                    fontWeight(500)
                    color(Color("#FAFAFA"))
                    marginBottom(6.px)
                    property("white-space", "nowrap")
                    property("overflow", "hidden")
                    property("text-overflow", "ellipsis")
                    lineHeight("1.3")
                }
            }) { Text(analysis.videoTitle) }

            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(12.px)
                    fontSize(11.px)
                    color(Color("#71717A"))
                }
            }) {
                Span({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(4.px)
                    }
                }) {
                    Text("🎬")
                    Text(analysis.videoId)
                }
                Span({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(4.px)
                    }
                }) {
                    Text("📅")
                    Text(formatDate(analysis.analyzedAt))
                }
            }
        }

        // Score breakdown with mini bars
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(12.px)
            }
        }) {
            MiniScoreBar("Title", analysis.titleScore)
            MiniScoreBar("Thumb", analysis.thumbnailScore)
        }

        // Arrow with hover effect
        Div({
            style {
                width(32.px)
                height(32.px)
                borderRadius(8.px)
                backgroundColor(Color("rgba(255, 255, 255, 0.05)"))
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                flexShrink(0)
                property("transition", "all 0.2s ease")
            }
        }) {
            Span({
                style {
                    color(Color("#A1A1AA"))
                    fontSize(14.px)
                }
            }) { Text("→") }
        }
    }
}

@Composable
private fun ScoreCircle(score: Int) {
    val color = getScoreColor(score)
    val glowColor = when {
        score >= 80 -> "rgba(34, 197, 94, 0.4)"
        score >= 60 -> "rgba(132, 204, 22, 0.4)"
        score >= 40 -> "rgba(245, 158, 11, 0.4)"
        else -> "rgba(239, 68, 68, 0.4)"
    }

    // Score circle with subtle glow
    Div({
        style {
            width(48.px)
            height(48.px)
            borderRadius(10.px)
            backgroundColor(Color(color))
            property("box-shadow", "0 4px 12px $glowColor")
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            color(Color.white)
            fontFamily("'SF Mono', Monaco, monospace")
            fontSize(18.px)
            fontWeight(600)
            flexShrink(0)
        }
    }) { Text("$score") }
}

@Composable
private fun MiniScoreBar(label: String, score: Int) {
    val color = getScoreColor(score)

    Div({
        style {
            minWidth(60.px)
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                marginBottom(4.px)
            }
        }) {
            Span({
                style {
                    fontSize(10.px)
                    color(Color("#71717A"))
                    fontWeight(500)
                }
            }) { Text(label) }

            Span({
                style {
                    fontFamily("'SF Mono', Monaco, monospace")
                    fontSize(11.px)
                    fontWeight(600)
                    color(Color(color))
                }
            }) { Text("$score") }
        }

        // Progress bar - solid color
        Div({
            style {
                height(3.px)
                backgroundColor(Color("#27272A"))
                borderRadius(2.px)
                property("overflow", "hidden")
            }
        }) {
            Div({
                style {
                    width(score.percent)
                    height(100.percent)
                    backgroundColor(Color(color))
                    borderRadius(2.px)
                }
            })
        }
    }
}

@Composable
private fun EmptyHistoryState() {
    Div({
        classes("card", "scale-in")
        style {
            textAlign("center")
            padding(48.px, 24.px)
        }
    }) {
        // Simple icon
        Span({
            style {
                fontSize(48.px)
                display(DisplayStyle.Block)
                marginBottom(16.px)
            }
        }) { Text("📋") }

        H3({
            style {
                fontSize(18.px)
                fontWeight(600)
                color(Color("#FAFAFA"))
                marginBottom(8.px)
            }
        }) { Text("No analyses yet") }

        P({
            style {
                color(Color("#A1A1AA"))
                fontSize(14.px)
                maxWidth(320.px)
                margin(0.px, "auto".unsafeCast<CSSNumeric>())
                lineHeight("1.5")
            }
        }) {
            Text("Your analyzed videos will appear here. Start by analyzing your first YouTube video!")
        }

        // CTA hint
        Div({
            style {
                marginTop(24.px)
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.Center)
                alignItems(AlignItems.Center)
                gap(8.px)
            }
        }) {
            Span({
                style {
                    color(Color("#71717A"))
                    fontSize(13.px)
                }
            }) { Text("Go to") }

            Span({
                style {
                    color(Color("#FF0000"))
                    fontSize(13.px)
                    fontWeight(500)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(4.px)
                }
            }) {
                Text("Analyze")
                Text("→")
            }
        }
    }
}

private fun getScoreColor(score: Int): String = when {
    score >= 80 -> "#22C55E"
    score >= 60 -> "#84CC16"
    score >= 40 -> "#F59E0B"
    else -> "#EF4444"
}
